import React from 'react'
import { Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Users } from 'lucide-react'
import { useSearchTokens } from '../../SearchScreen/searchDesignTokens'

const ShareholderTable = ({ personnes }) => {
  const { t, i18n } = useTranslation()
  const dk = useSearchTokens()

  if (!personnes || personnes.length === 0) return null

  const lang = i18n.language?.split('-')[0]
  const getLocalizedText = (textObj) => {
    if (!textObj) return null
    if (typeof textObj === 'string') return textObj
    return textObj[lang] || textObj.en || textObj.fr || textObj.ar || null
  }

  const formatShares = (person) => {
    if (person.pourcentage_capital != null) return `${person.pourcentage_capital}%`
    if (person.nombre_parts != null) return Number(person.nombre_parts).toLocaleString('fr-FR')
    if (person.parts != null) return Number(person.parts).toLocaleString('fr-FR')
    return null
  }

  const hasShares = personnes.some(
    (p) => p.pourcentage_capital != null || p.nombre_parts != null || p.parts != null
  )

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: '12px',
        border: `1px solid ${dk.border}`,
        backgroundColor: dk.surface,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Users size={16} color={dk.gold} />
        <Typography
          sx={{
            fontSize: '0.82rem',
            fontWeight: 700,
            color: dk.text1,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {t('personnes', 'Associés & Dirigeants')}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.68rem',
            fontWeight: 600,
            color: dk.text3,
            backgroundColor: dk.chipBg,
            borderRadius: '10px',
            px: 0.8,
            py: '1px',
          }}
        >
          {personnes.length}
        </Typography>
      </Box>

      {/* Table header */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: hasShares ? '1fr 1fr auto' : '1fr 1fr',
          gap: 1,
          px: 1.5,
          py: 0.8,
          borderRadius: '6px',
          backgroundColor: dk.elevated,
          mb: 0.5,
        }}
      >
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {t('nom', 'Nom')}
        </Typography>
        <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {t('qualite', 'Qualité')}
        </Typography>
        {hasShares && (
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'right' }}>
            {t('parts', 'Parts')}
          </Typography>
        )}
      </Box>

      {/* Rows */}
      {personnes.map((person, idx) => {
        const name = getLocalizedText(person.nom_complet) || person.nom || person.name
        const subText = [person.nationalite, person.cin || person.passeport].filter(Boolean).join(' · ')
        const shares = formatShares(person)
        const sharesLabel = person.pourcentage_capital != null ? `${person.pourcentage_capital}%` : shares

        return (
          <Box
            key={idx}
            sx={{
              display: 'grid',
              gridTemplateColumns: hasShares ? '1fr 1fr auto' : '1fr 1fr',
              gap: 1,
              px: 1.5,
              py: 1,
              borderRadius: '6px',
              transition: 'background-color 0.15s',
              borderBottom: idx < personnes.length - 1 ? `1px solid ${dk.borderLight}` : 'none',
              '&:hover': { backgroundColor: dk.hover },
            }}
          >
            {/* Name + sub-text */}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  color: dk.text1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {name || '-'}
              </Typography>
              {subText && (
                <Typography
                  sx={{
                    fontSize: '0.68rem',
                    color: dk.text3,
                    mt: 0.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {subText}
                </Typography>
              )}
            </Box>

            {/* Role */}
            <Typography
              sx={{
                fontSize: '0.78rem',
                color: dk.text2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                alignSelf: 'center',
              }}
            >
              {person.role || person.qualite || '-'}
            </Typography>

            {/* Shares / % */}
            {hasShares && (
              <Typography
                sx={{
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: sharesLabel ? dk.gold : dk.text3,
                  textAlign: 'right',
                  minWidth: 60,
                  alignSelf: 'center',
                }}
              >
                {sharesLabel || '-'}
              </Typography>
            )}
          </Box>
        )
      })}
    </Box>
  )
}

export default ShareholderTable
